import React, { useCallback, useEffect, useState } from "react";
import { BotonVolver } from "@/components/BotonVolver";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { hoyISO, sumarDias } from "@/lib/zona";
import { fechaLarga } from "@/lib/fechas";
import { cadaCuantoTitulo, estadoDe, importeTxt } from "@/lib/recordatorios";
import { Asistente } from "@/components/recordatorios/Asistente";
import { Confirmar } from "@/components/recordatorios/Confirmar";

const TABS = [["curso", "En curso", "No tienes recordatorios en curso."], ["pausado", "Pausados", "No tienes recordatorios pausados."], ["terminado", "Terminados", "No tienes recordatorios terminados."]];
const tabDe = (g) => ({ curso: "curso", pausado: "pausado" }[estadoDe(g).id] || "terminado");

export const EstadoEtiqueta = ({ g, testid }) => {
  const e = estadoDe(g); const I = Icons[e.icon];
  return <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${e.cls}`}><I size={16} /> {e.label}</span>;
};

const Tarjeta = ({ g, onClick }) => (
  <button data-testid={`rp-item-${g.id}`} onClick={onClick} className="w-full text-left bg-white border border-[#E5DFD5] rounded-2xl p-4 flex items-center gap-3 hover:border-[#3A6B56] transition-colors">
    <div className="flex-1 min-w-0 space-y-1">
      <EstadoEtiqueta g={g} testid={`rp-item-estado-${g.id}`} />
      <p className="font-heading font-bold text-lg text-[#1C2421]">{g.titulo}</p>
      <p className="text-base text-[#4A5550]">{cadaCuantoTitulo(g)}{g.importe != null ? ` · ${importeTxt(g.importe)}` : ""}</p>
      {g.proxima && <p className="text-base font-semibold text-[#3A6B56]">Próximo aviso: {fechaLarga(g.proxima)}</p>}
    </div>
    <Icons.ChevronRight size={22} className="text-[#C4BDB0] shrink-0" />
  </button>
);

const conImporte = (g) => g.importe != null && g.importe !== "";
const pagoPendiente = (g) => conImporte(g) && g.estado === "activa" && !!g.proxima;

const PagoCard = ({ g, onAbrir, onHecho }) => {
  const hoy = hoyISO();
  const estado = g.proxima < hoy ? "Pendiente · fecha pasada" : g.proxima === hoy ? "Pendiente · hoy" : "Próximo pago";
  return (
    <div data-testid={`pago-item-${g.id}`} className="bg-white border border-[#E5DFD5] rounded-2xl p-4 space-y-3">
      <button data-testid={`pago-abrir-${g.id}`} onClick={onAbrir} className="w-full text-left flex items-center gap-3">
        <span className="flex-1 min-w-0 space-y-0.5">
          <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${g.proxima <= hoy ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#F2EDE4] text-[#4A5550]"}`} data-testid={`pago-estado-${g.id}`}>{estado}</span>
          <span className="block font-heading font-bold text-lg text-[#1C2421]">{g.concepto || g.titulo}</span>
          <span className="block text-base text-[#4A5550]" data-testid={`pago-datos-${g.id}`}><b className="text-[#1C2421]">{importeTxt(g.importe)}</b> · {fechaLarga(g.proxima)}</span>
        </span>
        <Icons.ChevronRight size={22} className="text-[#C4BDB0] shrink-0" />
      </button>
      <button data-testid={`pago-hecho-${g.id}`} onClick={onHecho} className="min-h-[48px] px-5 rounded-xl bg-[#3A6B56] text-white font-semibold text-base inline-flex items-center gap-2"><Icons.Check size={18} /> Marcar como hecho</button>
    </div>
  );
};

const Papelera = ({ lista, onVolver, onChange }) => {
  const [borrar, setBorrar] = useState(null);
  const accion = async (g, fn, ok) => {
    try { await fn(); toast.success(ok); onChange(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo hacer."); }
  };
  return (
    <div className="space-y-5" data-testid="rp-papelera">
      <button data-testid="rp-papelera-volver" onClick={onVolver} className="min-h-[48px] inline-flex items-center gap-1 text-base font-semibold text-[#3A6B56]"><Icons.ChevronLeft size={20} /> Volver a Pagos y recordatorios</button>
      <h1 className="font-heading text-3xl font-bold text-[#1C2421]">Papelera</h1>
      <p className="text-base text-[#4A5550]">Estos recordatorios no generan avisos. Se quedan aquí hasta que tú los elimines.</p>
      {!lista.length ? <p data-testid="rp-papelera-vacia" className="text-base text-[#6E7A74] bg-white border border-dashed border-[#E5DFD5] rounded-2xl p-5 text-center">La papelera está vacía.</p> : lista.map((g) => (
        <div key={g.id} data-testid={`rp-papelera-item-${g.id}`} className="bg-white border border-[#E5DFD5] rounded-2xl p-4 space-y-3">
          <p className="font-heading font-bold text-lg text-[#1C2421]">{g.titulo}</p>
          <div className="flex flex-wrap gap-2">
            <button data-testid={`rp-recuperar-${g.id}`} onClick={() => accion(g, () => api.post(`/gestiones/${g.id}/estado`, { accion: "recuperar" }), "Recuperado. Lo encontrarás en Pausados.")} className="min-h-[52px] px-5 rounded-xl bg-[#3A6B56] text-white font-semibold text-base">Recuperar</button>
            <button data-testid={`rp-eliminar-def-${g.id}`} onClick={() => setBorrar(g)} className="min-h-[52px] px-5 rounded-xl bg-white border border-[#FCA5A5] text-[#B91C1C] font-semibold text-base">Eliminar definitivamente</button>
          </div>
        </div>
      ))}
      {borrar && (
        <Confirmar testid="rp-confirmar-eliminar" titulo="¿Eliminar definitivamente este recordatorio?" texto="Se borrarán también todos sus datos y su actividad. Esta acción no se puede deshacer."
          cancelar="Conservar" aceptar="Eliminar definitivamente" peligro onCancelar={() => setBorrar(null)}
          onAceptar={() => { const g = borrar; setBorrar(null); accion(g, () => api.delete(`/gestiones/${g.id}`), "Eliminado definitivamente"); }} />
      )}
    </div>
  );
};

export default function Gestiones() {
  const navigate = useNavigate();
  const [qs, setQs] = useSearchParams();
  const [gs, setGs] = useState(null);
  const [tab, setTab] = useState("curso");
  const cargar = useCallback(() => api.get("/gestiones").then((r) => setGs(r.data)).catch(() => setGs([])), []);
  useEffect(() => { cargar(); window.addEventListener("aldia-gestiones", cargar); return () => window.removeEventListener("aldia-gestiones", cargar); }, [cargar]);
  const nuevo = qs.get("nuevo") === "1";
  const abrirNuevo = () => setQs({ nuevo: "1" });
  const vivos = (gs || []).filter((g) => !g.en_papelera);
  const papelera = (gs || []).filter((g) => g.en_papelera);
  if (qs.get("papelera") === "1") return <Papelera lista={papelera} onVolver={() => { setQs({}); setTab("terminado"); }} onChange={cargar} />;
  const pagos = vivos.filter(pagoPendiente).sort((a, b) => a.proxima.localeCompare(b.proxima));
  const pagosOtros = vivos.filter((g) => conImporte(g) && !pagoPendiente(g));
  const recs = vivos.filter((g) => !conImporte(g));
  const lista = recs.filter((g) => tabDe(g) === tab);
  const hecho = async (g) => {
    try {
      const { data } = await api.post(`/gestiones/${g.id}/realizar-proximo`);
      toast.success("Pago marcado como hecho", { description: data.proxima ? `El siguiente será el ${fechaLarga(data.proxima)}.` : undefined });
      cargar(); window.dispatchEvent(new Event("aldia-gestiones"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo marcar."); }
  };
  const vacioTxt = TABS.find((t) => t[0] === tab)[2];
  return (
    <div className="space-y-6" data-testid="gestiones-page">
      <BotonVolver porDefecto="/" testid="gestiones-volver" />
      <header className="aldia-fade-up space-y-2">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C2421]">Pagos y recordatorios periódicos</h1>
        <p className="text-lg text-[#4A5550]">Organiza pagos, cuotas y avisos que se repiten cada semana, mes o año.</p>
      </header>
      {gs !== null && vivos.length > 0 && (
        <button data-testid="rp-crear-principal" onClick={abrirNuevo} className="w-full min-h-[64px] rounded-2xl bg-[#3A6B56] text-white font-bold text-xl inline-flex items-center justify-center gap-2 shadow-md hover:bg-[#2E5645]">
          <Icons.Plus size={26} /> Crear recordatorio periódico
        </button>
      )}
      {gs === null ? <p className="text-lg text-[#6E7A74]">Cargando…</p> : !vivos.length ? (
        <div data-testid="rp-vacio-total" className="bg-white border border-[#E5DFD5] rounded-3xl p-6 text-center space-y-4">
          <Icons.CalendarClock size={48} className="mx-auto text-[#3A6B56]" />
          <p className="text-xl font-semibold text-[#1C2421]">Todavía no tienes ningún recordatorio periódico.</p>
          <p className="text-lg text-[#4A5550]">Puedes usarlo para el alquiler, una cuota, un recibo o cualquier tarea que se repita.</p>
          <button data-testid="rp-crear-primero" onClick={abrirNuevo} className="w-full min-h-[60px] rounded-2xl bg-[#3A6B56] text-white font-bold text-xl">Crear el primero</button>
          {papelera.length > 0 && <button data-testid="rp-ir-papelera" onClick={() => setQs({ papelera: "1" })} className="min-h-[48px] text-base font-semibold text-[#4A5550] underline">Papelera ({papelera.length})</button>}
        </div>
      ) : (<>
        <section className="space-y-3" data-testid="pagos-pendientes">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#1C2421] flex items-center gap-2"><Icons.Wallet size={22} className="text-[#3A6B56]" /> Pagos pendientes</h2>
          {!pagos.length ? <p data-testid="pagos-vacio" className="text-lg text-[#4A5550] bg-white border border-dashed border-[#E5DFD5] rounded-2xl p-5 text-center">No tienes pagos pendientes.</p>
            : pagos.map((g) => <PagoCard key={g.id} g={g} onAbrir={() => navigate(`/gestiones/${g.id}`)} onHecho={() => hecho(g)} />)}
          {pagosOtros.length > 0 && (
            <details data-testid="pagos-otros" className="bg-[#FAF8F5] rounded-2xl px-4 py-2">
              <summary className="min-h-[44px] flex items-center text-base font-semibold text-[#4A5550] cursor-pointer">Pagos pausados o terminados ({pagosOtros.length})</summary>
              <div className="space-y-3 pb-2">{pagosOtros.map((g) => <Tarjeta key={g.id} g={g} onClick={() => navigate(`/gestiones/${g.id}`)} />)}</div>
            </details>
          )}
        </section>
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#1C2421] flex items-center gap-2 pt-2" data-testid="recordatorios-periodicos"><Icons.Repeat size={22} className="text-[#3A6B56]" /> Recordatorios periódicos</h2>
        <div className="grid grid-cols-3 gap-1.5 bg-[#F2EDE4] rounded-2xl p-1.5" role="tablist" data-testid="gestiones-tabs">
          {TABS.map(([id, l]) => (
            <button key={id} role="tab" aria-selected={tab === id} data-testid={`gestiones-tab-${id}`} onClick={() => setTab(id)}
              className={`min-h-[52px] rounded-xl font-semibold text-base ${tab === id ? "bg-white text-[#1C2421] shadow-sm underline underline-offset-4 decoration-2 decoration-[#3A6B56]" : "text-[#4A5550]"}`}>
              {l} ({recs.filter((g) => tabDe(g) === id).length})
            </button>
          ))}
        </div>
        {!lista.length ? <p data-testid="gestiones-vacio" className="text-lg text-[#4A5550] bg-white border border-dashed border-[#E5DFD5] rounded-2xl p-5 text-center">{vacioTxt}</p>
          : <div className="space-y-3">{lista.map((g) => <Tarjeta key={g.id} g={g} onClick={() => navigate(`/gestiones/${g.id}`)} />)}</div>}
        {tab === "terminado" && <button data-testid="rp-ir-papelera" onClick={() => setQs({ papelera: "1" })} className="min-h-[48px] inline-flex items-center gap-2 text-base font-semibold text-[#4A5550]"><Icons.Trash2 size={18} /> Papelera ({papelera.length})</button>}
      </>)}
      {nuevo && <Asistente onCerrar={() => { setQs({}); cargar(); }} onVer={(id) => navigate(`/gestiones/${id}`, { replace: true })} />}
    </div>
  );
}

// Acceso compacto en Inicio (solo si hay recordatorios en curso).
export const GestionesLink = ({ onClick }) => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    const cargar = () => Promise.all([api.get("/gestiones"), api.get("/gestiones/ocurrencias")]).then(([g, o]) => {
      const lim = sumarDias(hoyISO(), 30);
      setInfo({ activas: g.data.filter((x) => x.estado === "activa" && !x.en_papelera).length, proximas: g.data.filter((x) => x.proxima && x.proxima <= lim).length, pend: o.data.length });
    }).catch(() => {});
    cargar();
    window.addEventListener("aldia-gestiones", cargar);
    return () => window.removeEventListener("aldia-gestiones", cargar);
  }, []);
  if (!info || !info.activas) return null;
  const txt = info.pend ? `${info.pend} ${info.pend === 1 ? "pendiente" : "pendientes"}` : `${info.proximas} ${info.proximas === 1 ? "próximo" : "próximos"}`;
  return (
    <button data-testid="home-gestiones-link" onClick={onClick}
      className="w-full bg-white border border-[#E5DFD5] rounded-2xl px-4 py-3 flex items-center gap-3 hover:border-[#3A6B56] transition-colors text-left">
      <Icons.Repeat size={20} className="text-[#3A6B56] shrink-0" />
      <p className="flex-1 text-base font-semibold text-[#1C2421]">Pagos y recordatorios periódicos · <span className={info.pend ? "text-[#B45309]" : "text-[#3A6B56]"}>{txt}</span></p>
      <Icons.ChevronRight size={20} className="text-[#C4BDB0]" />
    </button>
  );
};
